import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRoleLevel,
  getUserRBACContext,
} from "helpers/utils/rbac";
import { useSelector } from "react-redux";

/**
 * Main RBAC hook
 * @returns {Object} RBAC utilities
 */
export const useRBAC = () => {
  const loginState = useSelector(state => state.Login);
  const rbacContext = getUserRBACContext(loginState);

  // Check if user has the special "Admin support" role (by ID)
  const hasAdminSupportRole = (loginState?.user?.roles || []).some(
    role => role.id === "cmlkxe32c007tnsb1moihnyu8",
  );

  return {
    // User info
    userId: rbacContext.userId,
    roleLevel: rbacContext.roleLevel,
    userRoleLevel: rbacContext.roleLevel,

    // Permission checkers
    can: permission => hasPermission(rbacContext.permissions, permission),
    canAny: permissions => hasAnyPermission(rbacContext.permissions, permissions),
    canAll: permissions => hasAllPermissions(rbacContext.permissions, permissions),

    // Role level checker
    hasRole: requiredLevel => hasRoleLevel(rbacContext.roleLevel, requiredLevel),

    // Common dynamic visibility checks
    // Show Source/SubSource if: roleLevel <= 2 OR user is "Admin support"
    showSourceSubSource: hasRoleLevel(rbacContext.roleLevel, 2) || hasAdminSupportRole,

    // Raw data
    permissions: rbacContext.permissions,
    tempPermissions: rbacContext.tempPermissions,
  };
};

/**
 * Hook to check single permission
 * @param {String} permission - Permission to check
 * @returns {Boolean}
 */
export const usePermission = permission => {
  const { can } = useRBAC();
  return can(permission);
};

/**
 * Hook to check multiple permissions (OR logic)
 * @param {Array} permissions - Array of permissions
 * @returns {Boolean}
 */
export const useAnyPermission = permissions => {
  const { canAny } = useRBAC();
  return canAny(permissions);
};

/**
 * Hook to check multiple permissions (AND logic)
 * @param {Array} permissions - Array of permissions
 * @returns {Boolean}
 */
export const useAllPermissions = permissions => {
  const { canAll } = useRBAC();
  return canAll(permissions);
};
